/**
 * Kontora BFF – Navigation & Menu-Definition
 *
 * REFACTORING (Round 69): Navigation radikal vereinfacht.
 *
 * Vorher: 1085 Zeilen, 147 Labels/Items, viele Redundanzen zwischen
 *          Operativ und Buchhaltung, 4+ Gruppen pro Sektion.
 * Nachher: Klare Workflow-Trennung, max 6 Items pro Gruppe,
 *          redundante Items entfernt.
 *
 * Architektur:
 *   - NavigationContext   = Top-Level-Bereich (Übersicht, Operativ, Buchhaltung, Berater, Mandant, System)
 *   - NavigationSection   = Bereichs-Block innerhalb der Sidebar (z. B. "Verkauf")
 *   - NavigationGroup     = logische Untergruppe, idealerweise als Workflow-Schritt
 *   - NavigationLeaf      = einzelner Menüpunkt
 *
 * Sichtbarkeits-Regeln:
 *   1. `requiredRoleLevel` filtert Sections hart (z. B. Buchhaltung = manager+).
 *   2. `requiredPermissions` macht Items nur sichtbar, wenn der User mind. eine
 *      der Permissions besitzt. Items ohne Berechtigung werden komplett
 *      ausgeblendet (kein "Gesperrt"-Badge mehr).
 *   3. `requiredFeature` blendet Items aus, wenn das Feature im Tenant nicht
 *      aktiviert ist.
 *   4. `comingSoon` zeigt das Item mit "In Vorbereitung"-Badge an.
 *   5. `powerMode` (nur SUPER_ADMIN) ignoriert Permission- und Feature-Checks
 *      und zeigt alle Items – für Diagnose und Plattform-Administration.
 *   6. Der `activeContext` filtert Sections auf den gewählten Top-Level-Bereich.
 *
 * VERBESSERUNGEN (Round 69):
 *   - Operativ-Bereich von 3 Sektionen beibehalten, aber redundante Items entfernt
 *     (Eingangsbelege, Offene Posten, Mahnungen → nur in Buchhaltung)
 *   - Buchhaltung bleibt mit Workflow-Schritten (Erfassen → Abschließen)
 *   - Mandant von 4 auf 3 Gruppen konsolidiert
 *     (Stammdaten / Zugriff & Sicherheit / Kommunikation & Reporting)
 *   - System von 4 auf 2 Gruppen reduziert (Betrieb / Beobachtbarkeit)
 *   - Steuerberater von 2 auf 1 Gruppe konsolidiert
 */

import { hasAnyPermission } from './permissions';
import { isKdpFoundationEnabled } from '../server/features/deferred-features';
import { hasRoleLevel } from './roles';

// ============================================================
// KONTEXTE (Top-Level-Bereiche)
// ============================================================

export type NavigationContext =
  | 'overview'
  | 'operations'
  | 'accounting'
  | 'advisor'
  | 'tenant'
  | 'system';

export type RoleLevel = 'superuser' | 'admin' | 'manager' | 'user' | 'readonly';

export type NavigationAccent =
  | 'work'
  | 'operations'
  | 'accounting'
  | 'advisor'
  | 'tenant'
  | 'system';

export interface NavigationContextMeta {
  label: string;
  shortLabel: string;
  description: string;
  accent: NavigationAccent;
  requiredRoleLevel: RoleLevel;
  href: string;
  icon: string;
}

export const NAVIGATION_CONTEXTS: Record<NavigationContext, NavigationContextMeta> = {
  overview: {
    label: 'Übersicht',
    shortLabel: 'Übersicht',
    description: 'Startseite, Aufgaben und Benachrichtigungen.',
    accent: 'work',
    requiredRoleLevel: 'readonly',
    href: '/dashboard',
    icon: 'D',
  },
  operations: {
    label: 'Operativ',
    shortLabel: 'Operativ',
    description: 'Tägliche Arbeit – Verkauf, Einkauf, Belege, Dokumente.',
    accent: 'operations',
    requiredRoleLevel: 'readonly',
    href: '/dashboard?ctx=operations',
    icon: 'O',
  },
  accounting: {
    label: 'Buchhaltung',
    shortLabel: 'Buchhaltung',
    description: 'Erfassen – Abgleichen – Buchen – Steuern – Auswerten – Abschließen.',
    accent: 'accounting',
    requiredRoleLevel: 'manager',
    href: '/reports?ctx=accounting',
    icon: 'B',
  },
  advisor: {
    label: 'Steuerberater',
    shortLabel: 'Berater',
    description: 'Zusammenarbeit mit der Steuerkanzlei.',
    accent: 'advisor',
    requiredRoleLevel: 'readonly',
    href: '/steuerberater',
    icon: 'S',
  },
  tenant: {
    label: 'Mandant',
    shortLabel: 'Mandant',
    description: 'Mandant, Benutzer, Sicherheit und Konfiguration.',
    accent: 'tenant',
    requiredRoleLevel: 'admin',
    href: '/einstellungen/mandant',
    icon: 'M',
  },
  system: {
    label: 'System',
    shortLabel: 'System',
    description: 'Jobs, Logs, Exporte und Migration (nur Sysadmin).',
    accent: 'system',
    requiredRoleLevel: 'superuser',
    href: '/system/jobs',
    icon: 'X',
  },
};

export const NAVIGATION_CONTEXT_ORDER: NavigationContext[] = [
  'overview',
  'operations',
  'accounting',
  'advisor',
  'tenant',
  'system',
];

// ============================================================
// NAVIGATIONS-ELEMENTE
// ============================================================

export interface NavigationLeaf {
  label: string;
  href?: string;
  requiredPermissions?: readonly string[];
  requiredFeature?: string;
  comingSoon?: boolean;
  description?: string;
}

export interface NavigationGroup {
  label: string;
  description?: string;
  items: NavigationLeaf[];
}

export interface NavigationSection {
  label: string;
  icon: string;
  context: NavigationContext;
  requiredRoleLevel?: RoleLevel;
  description?: string;
  groups: NavigationGroup[];
}

// ============================================================
// RESOLVED (für UI)
// ============================================================

export interface ResolvedNavigationLeaf extends NavigationLeaf {
  isVisible: boolean;
  isDisabled: boolean;
  disabledReason?: string;
  isActive: boolean;
  isComingSoon: boolean;
}

export interface ResolvedNavigationGroup {
  label: string;
  description?: string;
  items: ResolvedNavigationLeaf[];
}

export interface ResolvedNavigationSection {
  label: string;
  icon: string;
  context: NavigationContext;
  description?: string;
  groups: ResolvedNavigationGroup[];
  isVisible: boolean;
}

export interface BreadcrumbItem {
  label: string;
  href?: string;
  isCurrent?: boolean;
}

// ============================================================
// AKTIVER KONTEXT (Default + Helper)
// ============================================================

export const DEFAULT_NAVIGATION_CONTEXT: NavigationContext = 'overview';

export function isNavigationContext(value: unknown): value is NavigationContext {
  return (
    typeof value === 'string' && Object.prototype.hasOwnProperty.call(NAVIGATION_CONTEXTS, value)
  );
}

// ============================================================
// NAVIGATION ALS DATEN-DEFINITION
// ============================================================

const NAVIGATION: NavigationSection[] = [
  /* =====================================================================
   * KONTEXT: Übersicht
   * Persönliche Startseite mit Schnellzugriff
   * ===================================================================== */
  {
    label: 'Übersicht',
    icon: 'D',
    context: 'overview',
    requiredRoleLevel: 'readonly',
    description: 'Persönliche Startseite.',
    groups: [
      {
        label: 'Schnellzugriff',
        items: [
          { label: 'Finanzübersicht', href: '/dashboard' },
          { label: 'Kritische Aufgaben', href: '/aufgaben/kritisch' },
          { label: 'Benachrichtigungen', href: '/benachrichtigungen' },
          { label: 'Eigene Aufgaben', href: '/aufgaben' },
        ],
      },
      {
        label: 'Mandantenbetrieb',
        items: [
          { label: 'Jobs', href: '/system/jobs', requiredPermissions: ['jobs.manage'] },
          { label: 'Scheduler', href: '/system/scheduler', requiredPermissions: ['jobs.manage'] },
        ],
      },
    ],
  },

  /* =====================================================================
   * KONTEXT: Operativ
   * Tägliche Arbeit im Unternehmen – Verkaufsbelege, Einkauf, Dokumente
   * Die Buchhaltungs-Werkzeuge (Bankimporte, Mahnungen, Buchungen) sind
   * im Buchhaltungs-Bereich zu finden, da diese typischerweise von Buchhaltern
   * und Sachbearbeitern gemeinsam genutzt werden.
   * ===================================================================== */
  {
    label: 'Verkauf',
    icon: 'V',
    context: 'operations',
    requiredRoleLevel: 'readonly',
    description: 'Angebote, Rechnungen, Gutschriften und wiederkehrende Rechnungen.',
    groups: [
      {
        label: 'Verkaufsbelege',
        items: [
          {
            label: 'Angebote',
            href: '/verkauf/angebote',
            requiredPermissions: ['invoice.read'],
          },
          {
            label: 'Rechnungen',
            href: '/verkauf/rechnungen',
            requiredPermissions: ['invoice.read'],
          },
          {
            label: 'Gutschriften',
            href: '/verkauf/gutschriften',
            requiredPermissions: ['invoice.read'],
          },
          {
            label: 'Wiederkehrende Rechnungen',
            href: '/verkauf/recurring-invoices',
            requiredPermissions: ['recurring_invoice.read'],
            description: 'Automatische Rechnungsgenerierung',
          },
        ],
      },
    ],
  },
  {
    label: 'Einkauf',
    icon: 'E',
    context: 'operations',
    requiredRoleLevel: 'readonly',
    description: 'Eingangsbelege, Ausgaben und Kategorisierung.',
    groups: [
      {
        label: 'Eingangsbelege & Ausgaben',
        items: [
          {
            label: 'Eingangsbelege',
            href: '/einkauf/eingangsbelege',
            requiredPermissions: ['booking.read'],
          },
          {
            label: 'Ausgaben',
            href: '/einkauf/ausgaben',
            requiredPermissions: ['booking.read'],
          },
          ...(isKdpFoundationEnabled()
            ? [
                {
                  label: 'KDP',
                  href: '/einkauf/kdp',
                  requiredPermissions: ['booking.read'],
                } satisfies NavigationLeaf,
              ]
            : []),
          {
            label: 'Self-Billing',
            href: '/einkauf/self-billing',
            requiredPermissions: ['tax.evidence.manage'],
            description: 'Self-Billing-Validierung',
          },
          {
            label: 'Kategorien',
            href: '/einkauf/kategorien',
            requiredPermissions: ['category.read'],
          },
        ],
      },
    ],
  },
  {
    label: 'Stammdaten',
    icon: 'S',
    context: 'operations',
    requiredRoleLevel: 'readonly',
    groups: [
      {
        label: 'Kunden & Artikel',
        items: [
          { label: 'Partner', href: '/stammdaten/partner', requiredPermissions: ['invoice.read'] },
          {
            label: 'Produkte',
            href: '/stammdaten/produkte',
            requiredPermissions: ['invoice.read'],
          },
        ],
      },
    ],
  },
  {
    label: 'Dokumente',
    icon: 'D',
    context: 'operations',
    requiredRoleLevel: 'readonly',
    description: 'Dokumentenarchiv, Upload und Zuordnung.',
    groups: [
      {
        label: 'Archiv',
        items: [
          {
            label: 'Dokumentenarchiv',
            href: '/dokumente',
            requiredPermissions: ['document.read'],
          },
          {
            label: 'Upload',
            href: '/dokumente/upload',
            requiredPermissions: ['document.upload'],
          },
          {
            label: 'E-Rechnungen',
            href: '/dokumente/e-rechnungen',
            requiredPermissions: ['document.read'],
          },
          {
            label: 'Unzugeordnet',
            href: '/dokumente/unzugeordnet',
            requiredPermissions: ['document.read'],
          },
        ],
      },
    ],
  },

  /* =====================================================================
   * KONTEXT: Buchhaltung
   * Buchführungs-Workflow von Eingang bis Abschluss.
   * Struktur: Workflow-Phasen (Erfassen → Abgleichen → Buchen → Steuern → Auswerten → Abschließen).
   * Diese Sektion enthält ALLE buchhalterischen Tools – sie ist für Sachbearbeiter
   * und Buchhalter die zentrale Anlaufstelle.
   * ===================================================================== */
  {
    label: 'Buchhaltung',
    icon: 'B',
    context: 'accounting',
    requiredRoleLevel: 'manager',
    description: 'Buchführungs-Workflow von Eingang bis Abschluss.',
    groups: [
      {
        label: 'Erfassen',
        description: 'Belege und Daten einsammeln.',
        items: [
          {
            label: 'Eingangsbelege',
            href: '/einkauf/eingangsbelege',
            requiredPermissions: ['booking.read'],
          },
          {
            label: 'Bankimporte',
            href: '/importe',
            requiredPermissions: ['import.read'],
            requiredFeature: 'banking',
          },
          {
            label: 'CSV-Profile',
            href: '/importe/profile',
            requiredPermissions: ['import.read'],
            requiredFeature: 'banking',
          },
          {
            label: 'Kassenbuch',
            href: '/buchhaltung/kassenbuch',
            requiredPermissions: ['cashbook.read'],
            requiredFeature: 'cashbook',
          },
          {
            label: 'Self-Billing',
            href: '/einkauf/self-billing',
            requiredPermissions: ['tax.evidence.manage'],
          },
        ],
      },
      {
        label: 'Abgleichen',
        description: 'Bank, Kasse und offene Posten abstimmen.',
        items: [
          {
            label: 'Bankumsätze',
            href: '/buchhaltung/bankumsaetze',
            requiredPermissions: ['bank_account.read'],
            requiredFeature: 'banking',
          },
          {
            label: 'Zahlungsabgleich',
            href: '/buchhaltung/zahlungsabgleich',
            requiredPermissions: ['bank_transaction.match'],
            requiredFeature: 'banking',
          },
          {
            label: 'Offene Posten',
            href: '/buchhaltung/offene-posten',
            requiredPermissions: ['booking.read'],
          },
          {
            label: 'Mahnungen',
            href: '/verkauf/mahnungen',
            requiredPermissions: ['report.read'],
            description: 'Überfällige offene Posten',
          },
          {
            label: 'Zahlungen',
            href: '/buchhaltung/zahlungen',
            requiredPermissions: ['booking.read'],
          },
          {
            label: 'Aufrechnungen',
            href: '/buchhaltung/aufrechnungen',
            requiredPermissions: ['invoice.tax_review'],
            description: 'Bestätigte Verrechnungen und gebundene Originalnachweise',
          },
          {
            label: 'Umbuchungen',
            href: '/buchhaltung/umbuchungen',
            requiredPermissions: ['bank_transfer.read'],
            requiredFeature: 'banking',
          },
          {
            label: 'Tagesabschluss',
            href: '/buchhaltung/kassenbuch#tagesabschluss',
            requiredPermissions: ['cashbook.read'],
            requiredFeature: 'cashbook',
          },
        ],
      },
      {
        label: 'Buchen',
        description: 'Vorschläge, Journal und Konten.',
        items: [
          {
            label: 'Buchungsvorschläge',
            href: '/buchhaltung/buchungsvorschlaege',
            requiredPermissions: ['booking.read'],
          },
          {
            label: 'Journal',
            href: '/buchhaltung/journal',
            requiredPermissions: ['booking.read'],
          },
          {
            label: 'Konten',
            href: '/buchhaltung/konten',
            requiredPermissions: ['booking.read'],
          },
          {
            label: 'Bankkonten',
            href: '/buchhaltung/bankkonten',
            requiredPermissions: ['bank_account.read'],
            requiredFeature: 'banking',
          },
        ],
      },
      {
        label: 'Steuern',
        description: 'Steuerlogik und Nachweise.',
        items: [
          {
            label: 'Steuergrundlagen',
            href: '/steuern/grundlagen',
            requiredPermissions: ['tax.read'],
          },
          {
            label: 'Tax Treatments',
            href: '/steuern/tax-treatments',
            requiredPermissions: ['tax.read'],
          },
          {
            label: 'Prüfungen / Nachweise',
            href: '/steuern/pruefungen',
            requiredPermissions: ['tax.read'],
          },
          {
            label: 'Rechnungshinweise',
            href: '/steuern/rechnungshinweise',
            requiredPermissions: ['tax.read'],
          },
          {
            label: 'Blockierte Steuerfälle',
            href: '/steuern/blockierte-faelle',
            requiredPermissions: ['tax.read'],
          },
          {
            label: 'UStVA Preview',
            href: '/reports/ustva-preview',
            requiredPermissions: ['report.read'],
          },
        ],
      },
      {
        label: 'Auswerten',
        description: 'Berichte, KPIs und Forecast.',
        items: [
          {
            label: 'Dashboard',
            href: '/reports/dashboard',
            requiredPermissions: ['report.read'],
          },
          {
            label: 'Alle Berichte',
            href: '/reports',
            requiredPermissions: ['report.read'],
            description: 'Gesamtübersicht aller Berichte',
          },
          {
            label: 'EUER',
            href: '/reports/euer',
            requiredPermissions: ['report.read'],
          },
          {
            label: 'OPOS',
            href: '/reports/opos',
            requiredPermissions: ['report.read'],
          },
          {
            label: 'Cashflow',
            href: '/reports/cashflow',
            requiredPermissions: ['report.read'],
          },
          {
            label: 'Profitability',
            href: '/reports/profitability',
            requiredPermissions: ['report.read'],
          },
          {
            label: 'Tax Risk',
            href: '/reports/tax-risk',
            requiredPermissions: ['report.read'],
          },
          {
            label: 'Budgets',
            href: '/einstellungen/budgets',
            requiredPermissions: ['booking.read'],
            requiredFeature: 'controlling',
          },
          {
            label: 'Snapshots',
            href: '/reports/snapshots',
            requiredPermissions: ['report.read'],
          },
        ],
      },
      {
        label: 'Abschließen',
        description: 'Perioden, Monats- und Jahresabschluss.',
        items: [
          {
            label: 'Perioden',
            href: '/buchhaltung/perioden',
            requiredPermissions: ['booking.read'],
          },
          {
            label: 'Monatsabschluss',
            href: '/reports/monthly-close',
            requiredPermissions: ['report.read'],
          },
          {
            label: 'Jahresabschluss',
            href: '/reports/monthly-close',
            requiredPermissions: ['booking.read'],
            comingSoon: true,
            description: 'Geplant für 2027',
          },
        ],
      },
    ],
  },

  /* =====================================================================
   * KONTEXT: Steuerberater
   * Zusammenarbeit mit der Steuerkanzlei (konsolidiert auf 1 Gruppe).
   * ===================================================================== */
  {
    label: 'Steuerberater',
    icon: 'S',
    context: 'advisor',
    description: 'Zusammenarbeit mit der Steuerkanzlei.',
    groups: [
      {
        label: 'Kanzlei-Zusammenarbeit',
        items: [
          {
            label: 'Übersicht',
            href: '/steuerberater',
            requiredPermissions: ['advisor.comment'],
            requiredFeature: 'advisor',
          },
          {
            label: 'Zugriff',
            href: '/steuerberater/zugriff',
            requiredPermissions: ['advisor.access.manage'],
            requiredFeature: 'advisor',
          },
          {
            label: 'Kommentare',
            href: '/steuerberater/kommentare',
            requiredPermissions: ['advisor.comment'],
            requiredFeature: 'advisor',
          },
          {
            label: 'Aufgaben',
            href: '/steuerberater/aufgaben',
            requiredPermissions: ['advisor.task.manage'],
            requiredFeature: 'advisor',
          },
          {
            label: 'Reviews',
            href: '/steuerberater/reviews',
            requiredPermissions: ['advisor.review'],
            requiredFeature: 'advisor',
          },
          {
            label: 'Kritische Aufgaben',
            href: '/aufgaben/kritisch',
            requiredPermissions: ['advisor.task.manage'],
            requiredFeature: 'advisor',
          },
        ],
      },
    ],
  },

  /* =====================================================================
   * KONTEXT: Mandant
   * Tenant-Administration (konsolidiert auf 3 Gruppen):
   *   1. Stammdaten         → Mandant, Partner, Produkte, Projekte
   *   2. Zugriff & Sicherheit → Benutzer, Rollen, 2FA, Audit-Log, Backups
   *   3. Kommunikation      → E-Mail, Design, Benachrichtigungen, Budgets
   * ===================================================================== */
  {
    label: 'Mandant',
    icon: 'M',
    context: 'tenant',
    requiredRoleLevel: 'admin',
    description: 'Stammdaten, Benutzer, Sicherheit und Konfiguration.',
    groups: [
      {
        label: 'Stammdaten',
        items: [
          {
            label: 'Mandant',
            href: '/einstellungen/mandant',
            requiredPermissions: ['tenant.read'],
          },
          {
            label: 'Business Units',
            href: '/einstellungen/business-units',
            requiredPermissions: ['business_unit.read'],
          },
          {
            label: 'Projekte',
            href: '/einstellungen/projekte',
            requiredPermissions: ['project.read'],
          },
          {
            label: 'Zahlungsbedingungen',
            href: '/einstellungen/zahlungsbedingungen',
            requiredPermissions: ['invoice.read'],
          },
          {
            label: 'Nummernkreise',
            href: '/einstellungen/nummernkreise',
            requiredPermissions: ['tenant.security.update'],
          },
        ],
      },
      {
        label: 'Zugriff & Sicherheit',
        items: [
          {
            label: 'Benutzer & Rollen',
            href: '/einstellungen/benutzer-rollen',
            requiredPermissions: ['user.read'],
          },
          {
            label: 'Sicherheit',
            href: '/einstellungen/sicherheit',
            requiredPermissions: ['security.read'],
          },
          {
            label: '2FA',
            href: '/einstellungen/2fa',
            requiredPermissions: ['security.read'],
          },
          {
            label: 'Break-Glass',
            href: '/einstellungen/break-glass',
            requiredPermissions: ['security.read'],
          },
          {
            label: 'Onboarding-Analytics',
            href: '/einstellungen/onboarding-analytics',
            requiredPermissions: ['tenant.read'],
            description: 'Workflow-Telemetrie (Starts, Completes, Cancels)',
          },
          {
            label: 'Audit-Log',
            href: '/einstellungen/audit-log',
            requiredPermissions: ['security.read'],
            description: 'Sicherheits- und Änderungsereignisse im Mandanten',
          },
          {
            label: 'Backups',
            href: '/einstellungen/backups',
            requiredPermissions: ['tenant.read'],
            description: 'Backups erstellen, verwalten und wiederherstellen',
          },
        ],
      },
      {
        label: 'Kommunikation & Reporting',
        items: [
          {
            label: 'Benachrichtigungen',
            href: '/einstellungen/benachrichtigungen',
            requiredPermissions: ['tenant.read'],
          },
          {
            label: 'E-Mail',
            href: '/einstellungen/email',
            requiredPermissions: ['email.settings.manage'],
          },
          {
            label: 'E-Mail-Vorlagen',
            href: '/einstellungen/email/templates',
            requiredPermissions: ['email.template.manage'],
          },
          {
            label: 'Design / Anzeige',
            href: '/einstellungen/design',
            requiredPermissions: ['tenant.read'],
          },
          {
            label: 'Budgets',
            href: '/einstellungen/budgets',
            requiredPermissions: ['booking.read'],
            requiredFeature: 'controlling',
          },
          {
            label: 'Allocations',
            href: '/einstellungen/allocations',
            requiredPermissions: ['booking.read'],
          },
          {
            label: 'User-Feedback',
            href: '/einstellungen/feedback',
            requiredPermissions: ['tenant.read'],
            description: 'Vom Widget gesammelte Rückmeldungen der Nutzer',
          },
        ],
      },
    ],
  },

  /* =====================================================================
   * KONTEXT: System (NUR SUPER_ADMIN)
   * Plattform-Administration (konsolidiert auf 2 Gruppen):
   *   1. Betrieb         → Jobs, Scheduler, Exporte, Migration
   *   2. Beobachtbarkeit  → Logs, Health, Audit-Anomalien
   * ===================================================================== */
  {
    label: 'System',
    icon: 'X',
    context: 'system',
    requiredRoleLevel: 'superuser',
    description: 'Betrieb, Jobs, Logs, Exporte und Migration.',
    groups: [
      {
        label: 'Betrieb',
        items: [
          {
            label: 'Jobs',
            href: '/system/jobs',
            requiredPermissions: ['jobs.manage'],
          },
          {
            label: 'Scheduler',
            href: '/system/scheduler',
            requiredPermissions: ['jobs.manage'],
          },
          {
            label: 'Exporte',
            href: '/exporte',
            requiredPermissions: ['export.read'],
          },
          {
            label: 'Migration',
            href: '/migration',
            requiredPermissions: ['migration.report.read'],
          },
          {
            label: 'Systemzustand',
            href: '/system/logs-health',
            requiredPermissions: ['jobs.manage'],
          },
        ],
      },
      {
        label: 'Beobachtbarkeit',
        items: [
          {
            label: 'System-Health',
            href: '/system/logs-health',
            requiredPermissions: ['jobs.manage'],
          },
          {
            label: 'Error-Logs',
            href: '/system/logs',
            requiredPermissions: ['error_log.read'],
            description: 'Backend- und Frontend-Fehler einsehen',
          },
          {
            label: 'Audit-Anomalien',
            href: '/admin/audit-anomalies',
          },
          {
            label: 'Tenant-Health',
            href: '/admin/tenant-health',
          },
          {
            label: 'Bulk-Operations-Log',
            href: '/admin/bulk-operation-log',
          },
        ],
      },
    ],
  },
];

// ============================================================
// HELPERS
// ============================================================

function normalizePathname(pathname: string) {
  const path = pathname.split(/[?#]/)[0] || '/';
  if (path === '/') {
    return path;
  }
  return path.endsWith('/') ? path.slice(0, -1) : path;
}

export function getNavigationDefinition() {
  return NAVIGATION;
}

export function resolveActiveContext(
  pathname: string,
  activeContext: NavigationContext,
  availableContexts: readonly NavigationContext[],
): NavigationContext {
  const currentPath = normalizePathname(pathname);
  const matchesCurrentContext = NAVIGATION.some(
    (section) =>
      section.context === activeContext &&
      section.groups.some((group) =>
        group.items.some((item) => {
          if (!item.href) return false;
          const path = normalizePathname(item.href);
          return currentPath === path || currentPath.startsWith(path + '/');
        }),
      ),
  );
  if (matchesCurrentContext) return activeContext;
  const inferred = deriveContextFromPath(currentPath);
  return availableContexts.includes(inferred) ? inferred : activeContext;
}

export function getNavigationContexts(): readonly NavigationContext[] {
  return NAVIGATION_CONTEXT_ORDER;
}

export function getAllowedContextsForRoles(
  roles: readonly string[] | null | undefined,
): NavigationContext[] {
  return NAVIGATION_CONTEXT_ORDER.filter((context) => {
    const meta = NAVIGATION_CONTEXTS[context];
    return hasRoleLevel(roles, meta.requiredRoleLevel);
  });
}

// ============================================================
// RESOLVE-NAVIGATION
// ============================================================

export interface ResolveNavigationOptions {
  pathname: string;
  permissions: readonly string[] | null | undefined;
  activeFeatures: readonly string[] | null | undefined | boolean;
  permissionsLoaded?: boolean;
  roles: readonly string[] | null | undefined;
  activeContext: NavigationContext;
  powerMode?: boolean;
}

/**
 * Bestimmt die für die Sidebar sichtbaren Sections.
 *
 *   1. Sections werden auf `activeContext` gefiltert.
 *   2. Sections werden auf `requiredRoleLevel` gefiltert.
 *   3. Items werden auf `requiredPermissions` und `requiredFeature` geprüft.
 *   4. Wenn `powerMode` aktiv ist (nur SUPER_ADMIN), werden 3. ignoriert
 *      und alle Items gezeigt.
 *   5. `comingSoon` blendet das Item sichtbar mit Badge ein.
 */
export function resolveNavigation({
  pathname,
  permissions,
  activeFeatures,
  permissionsLoaded = false,
  roles,
  activeContext,
  powerMode = false,
}: ResolveNavigationOptions): ResolvedNavigationSection[] {
  const currentPath = normalizePathname(pathname);
  const isLoaded = typeof activeFeatures === 'boolean' ? activeFeatures : permissionsLoaded;
  const features = Array.isArray(activeFeatures) ? activeFeatures : undefined;
  const activeHref = NAVIGATION.filter((section) => section.context === activeContext)
    .flatMap((section) => section.groups.flatMap((group) => group.items))
    .filter((item) => item.href && !item.href.includes('#'))
    .map((item) => normalizePathname(item.href!))
    .filter((path) => currentPath === path || currentPath.startsWith(`${path}/`))
    .sort((first, second) => second.length - first.length)[0];

  return NAVIGATION.filter((section) => {
    if (section.context !== activeContext) return false;
    if (section.requiredRoleLevel && !hasRoleLevel(roles, section.requiredRoleLevel)) {
      return false;
    }
    return true;
  })
    .map((section) => {
      const groups = section.groups
        .map<ResolvedNavigationGroup>((group) => {
          const items = group.items
            .map<ResolvedNavigationLeaf>((item) => {
              const featureAllowed =
                !!powerMode ||
                !features ||
                !item.requiredFeature ||
                features.includes(item.requiredFeature);

              const permissionAllowed =
                !!powerMode ||
                !item.requiredPermissions ||
                (isLoaded && hasAnyPermission(permissions, item.requiredPermissions));

              const isVisible = item.comingSoon
                ? featureAllowed
                : featureAllowed && permissionAllowed;
              const isDisabled = !permissionAllowed && !item.comingSoon;
              const isActive = Boolean(
                item.href &&
                !item.href.includes('#') &&
                normalizePathname(item.href) === activeHref,
              );

              return {
                ...item,
                isVisible,
                isDisabled,
                disabledReason: permissionAllowed
                  ? undefined
                  : 'Keine Berechtigung für diesen Bereich',
                isActive,
                isComingSoon: Boolean(item.comingSoon),
              };
            })
            .filter((item) => item.isVisible);

          return {
            label: group.label,
            description: group.description,
            items,
          };
        })
        .filter((group) => group.items.length > 0);

      if (groups.length === 0) return null;
      return {
        label: section.label,
        icon: section.icon,
        context: section.context,
        description: section.description,
        groups,
        isVisible: true,
      } as ResolvedNavigationSection;
    })
    .filter((section): section is ResolvedNavigationSection => section !== null);
}

// ============================================================
// CONTEXT → BEST STARTING PAGE
// ============================================================

/**
 * Ermittelt für einen gegebenen Path den passenden Top-Level-Kontext.
 * Wird z. B. beim ersten Login oder nach einer Deep-Link-Navigation genutzt,
 * um den aktiven Kontext automatisch zu wählen.
 */
export function deriveContextFromPath(pathname: string): NavigationContext {
  const normalized = normalizePathname(pathname);
  let bestMatch: { context: NavigationContext; length: number } | undefined;

  // Erstes Prefix-Match gegen Item-Hrefs jeder Section
  for (const section of NAVIGATION) {
    for (const group of section.groups) {
      for (const item of group.items) {
        if (!item.href) continue;
        const itemPath = normalizePathname(item.href.split('?')[0] ?? item.href);
        if (itemPath === '/' || itemPath === '') continue;
        if (normalized === itemPath || normalized.startsWith(`${itemPath}/`)) {
          if (!bestMatch || itemPath.length > bestMatch.length) {
            bestMatch = { context: section.context, length: itemPath.length };
          }
        }
      }
    }
  }

  if (bestMatch) return bestMatch.context;

  // Fallback: erstes passendes Prefix-Match
  if (
    normalized.startsWith('/verkauf') ||
    normalized.startsWith('/einkauf') ||
    normalized.startsWith('/dokumente')
  ) {
    return 'operations';
  }
  if (
    normalized.startsWith('/buchhaltung') ||
    normalized.startsWith('/steuern') ||
    normalized.startsWith('/reports') ||
    normalized.startsWith('/importe') ||
    normalized.startsWith('/aufgaben')
  ) {
    return 'accounting';
  }
  if (normalized.startsWith('/steuerberater')) {
    return 'advisor';
  }
  if (normalized.startsWith('/einstellungen') || normalized.startsWith('/stammdaten')) {
    return 'tenant';
  }
  if (
    normalized.startsWith('/system') ||
    normalized.startsWith('/exporte') ||
    normalized.startsWith('/migration') ||
    normalized.startsWith('/internal')
  ) {
    return 'system';
  }

  return DEFAULT_NAVIGATION_CONTEXT;
}

// ============================================================
// BREADCRUMBS
// ============================================================

export function buildBreadcrumbs(pathname: string): BreadcrumbItem[] {
  const currentPath = normalizePathname(pathname);
  if (currentPath === '/') {
    return [{ label: 'Start', isCurrent: true }];
  }

  const matches: BreadcrumbItem[] = [{ label: 'Übersicht', href: '/dashboard' }];

  const candidates = NAVIGATION.flatMap((section) =>
    section.groups.flatMap((group) => group.items.map((item) => ({ section, item }))),
  )
    .filter(({ item }) => item.href && !item.href.includes('#'))
    .sort(
      (first, second) =>
        normalizePathname(second.item.href!).length - normalizePathname(first.item.href!).length,
    );

  for (const { section, item } of candidates) {
    if (!item.href) continue;
    const itemPath = normalizePathname(item.href.split('?')[0] ?? item.href);
    if (currentPath === itemPath) {
      const contextMeta = NAVIGATION_CONTEXTS[section.context];
      if (contextMeta && section.context !== 'overview') {
        matches.push({ label: contextMeta.label });
      }
      matches.push({ label: item.label, href: item.href, isCurrent: true });
      return matches;
    }
    if (currentPath.startsWith(`${itemPath}/`)) {
      const contextMeta = NAVIGATION_CONTEXTS[section.context];
      if (contextMeta && section.context !== 'overview') {
        matches.push({ label: contextMeta.label });
      }
      matches.push({ label: item.label, href: item.href });
      const rest = currentPath.slice(itemPath.length + 1);
      if (itemPath === '/buchhaltung/aufrechnungen') {
        const parts = rest.split('/');
        const uuid = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;
        if (rest === 'neu') {
          matches.push({ label: 'Neue Aufrechnung', isCurrent: true });
          return matches;
        }
        if (
          uuid.test(parts[0] ?? '') &&
          (parts.length === 1 ||
            (parts.length === 3 &&
              parts[1] === 'korrekturen' &&
              (parts[2] === 'neu' || uuid.test(parts[2] ?? ''))))
        ) {
          matches.push({
            label: 'Gebuchte Aufrechnung',
            href: `${itemPath}/${parts[0]}`,
            isCurrent: parts.length === 1,
          });
          if (parts.length === 3)
            matches.push({
              label: parts[2] === 'neu' ? 'Berichtigung vorbereiten' : 'Gebuchte Berichtigung',
              isCurrent: true,
            });
          return matches;
        }
      }
      if (rest) {
        matches.push({
          label: rest
            .split('/')
            .map((part) => part.replace(/-/g, ' '))
            .join(' / '),
          isCurrent: true,
        });
      }
      return matches;
    }
  }

  return [
    { label: 'Übersicht', href: '/dashboard' },
    {
      label: currentPath
        .split('/')
        .filter(Boolean)
        .map((part) => part.replace(/-/g, ' '))
        .join(' / '),
      isCurrent: true,
    },
  ];
}
